import React, { useEffect } from "react";
import SeoRelatedLinks from "../../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "En drikkemeny bør være rask, tydelig og oppdatert",
  },
  {
    id: "why",
    label: "Hvorfor gir en digital drikkemeny mening for barer?",
  },
  {
    id: "use-cases",
    label: "Hvilke drikker fungerer spesielt godt i en digital meny?",
  },
  {
    id: "structure",
    label: "Hvordan bør en digital drikkemeny struktureres?",
  },
  {
    id: "updates",
    label: "Hva kan en bar oppdatere raskere online?",
  },
  {
    id: "mistakes",
    label: "Hvilke feil skjer med digitale drikkemenyer?",
  },
  {
    id: "recommendation",
    label: "Hvordan ville jeg startet som bareier?",
  },
  {
    id: "next-steps",
    label: "Hva bør du gjøre nå?",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper deg med å lage en digital drikkemeny",
  },
  {
    id: "faq",
    label: "FAQ om digitale drikkemenyer",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>

      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitalDrinksMenuBarNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Digital drikkemeny for barer: cocktails, vin og drikke online | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/digitale-getraenkekarte-bar",
          en: "/en/digital-drinks-menu-bar",
          no: "/no/digital-drikkemeny-bar",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digital drikkemeny for barer: administrer cocktails, vin og drikke
              online
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                En drikkemeny endres ofte mer enn en vanlig matmeny: nye
                cocktails, sesongdrikker, utsolgte viner, alkoholfrie
                alternativer, oppdaterte priser eller tidsbegrensede tilbud. Det
                er her en digital drikkemeny blir praktisk.
              </p>

              <p className="seoHero__text">
                I stedet for å trykke menyen på nytt eller bytte ut en PDF hver
                gang, kan baren oppdatere drikkevarer online. QR-koden forblir
                den samme, mens drikke, priser, beskrivelser og tilgjengelighet
                kan endres.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>En drikkemeny bør være rask, tydelig og oppdatert</h2>

          <p>
            Drikkevarer endres ofte på kort varsel. En vin blir utilgjengelig,
            en cocktail byttes ut for sesongen, en ny alkoholfri drikk legges
            til, eller baren vil fremheve bestemte anbefalinger.
          </p>

          <p>
            En digital drikkemeny hjelper med å holde denne informasjonen
            oppdatert uten å trykke på nytt eller erstatte filer igjen og igjen.
            Baren kan oppdatere drikkevarer online mens gjestene fortsetter å
            bruke den samme QR-koden.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Svakere</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Trykt drikkemeny blir raskt utdatert.
                </li>

                <li className="text__seo-li">
                  Utilgjengelige drikker forblir synlige.
                </li>

                <li className="text__seo-li">
                  Nye drikker vises for sent i menyen.
                </li>

                <li className="text__seo-li">
                  Flere PDF-versjoner sirkulerer.
                </li>

                <li className="text__seo-li">
                  Personalet må stadig forklare hva som er tilgjengelig.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Bedre</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Oppdater drikkevarer online.</li>

                <li className="text__seo-li">
                  Vis tilgjengelige drikker tydelig.
                </li>

                <li className="text__seo-li">
                  Legg til sesongtilbud raskt.
                </li>

                <li className="text__seo-li">
                  Bruk én sentral digital meny.
                </li>

                <li className="text__seo-li">Behold samme QR-kode.</li>
              </ul>
            </div>
          </div>

          <h3>Start med en liten digital drikkemeny</h3>

          <p>
            Legg først inn cocktails, vin eller alkoholfrie anbefalinger, og
            sjekk QR-kodevisningen på en smarttelefon. En fokusert førsteversjon
            er enklere å teste enn en lang drikkeliste med alle mulige
            kategorier.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor gir en digital drikkemeny mening for barer?</h2>

            <p>
              En klassisk drikkemeny fungerer godt når tilbudet er stabilt. Men
              mange barer, kafeer og restauranter endrer drikkevarer etter
              sesong, innkjøp, tilgjengelighet, tidspunkt på dagen eller konsept.
            </p>

            <p>
              En digital drikkemeny er spesielt nyttig når gjester raskt må
              forstå aktuelle valg: cocktails, vin, craft-drikker, alkoholfrie
              alternativer, kaffespesialiteter, aperitiffer, digestiffer eller
              husets anbefalinger.
            </p>

            <h3>Teknologi bør forbedre service og drift</h3>

            <p>
              En digital drikkemeny er nyttig når den hjelper baren med å
              presentere aktuelle valg tydeligere, redusere utdatert informasjon
              og støtte personalet under service.
            </p>

            <p>
              Den bør ikke erstatte god service. For vin, cocktails eller
              anbefalinger kan den forberede og støtte samtalen mellom gjest og
              personale.
            </p>
          </div>
        </section>

        <section id="use-cases" className="acquaintance">
          <h2>Hvilke drikker fungerer spesielt godt i en digital meny?</h2>

          <p>
            Ikke alle barer trenger en kompleks digital struktur. Men de fleste
            barer kan ha nytte av at drikker som endres ofte eller trenger mer
            forklaring, presenteres tydeligere.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Cocktailmeny.</li>
            <li className="text__seo-li">Vinkart.</li>
            <li className="text__seo-li">Alkoholfrie drikker.</li>
            <li className="text__seo-li">Sesongdrikker.</li>
            <li className="text__seo-li">Happy hour-tilbud.</li>
            <li className="text__seo-li">Kaffe og spesialiteter.</li>
            <li className="text__seo-li">Husets anbefalinger.</li>
            <li className="text__seo-li">Utilgjengelige drikker.</li>
          </ul>

          <p>
            En digital drikkemeny er sterkest når den gir kort orientering:
            smak, stil, styrke, alkoholfritt alternativ eller sesongkontekst.
          </p>

          <VisualIdea>
            Smarttelefon med en digital drikkemeny: cocktails, vin, alkoholfrie
            drikker og sesonganbefalinger som tydelige kategorier.
          </VisualIdea>
        </section>

        <section id="structure" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvordan bør en digital drikkemeny struktureres?</h2>

            <p>
              En god drikkemeny trenger orden. Gjester bør ikke måtte bla
              gjennom en lang blandet liste når de leter etter en cocktail, et
              glass vin eller en alkoholfri anbefaling.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Skill kategorier tydelig:</strong> For eksempel
                cocktails, vin, øl, alkoholfritt, kaffe og anbefalinger.
              </li>

              <li className="text__seo-li">
                <strong>Bruk korte beskrivelser:</strong> Forklar smak, stil
                eller spesielle ingredienser kort.
              </li>

              <li className="text__seo-li">
                <strong>Hold priser synlige:</strong> Gjester skal ikke måtte
                lete etter dem.
              </li>

              <li className="text__seo-li">
                <strong>Administrer tilgjengelighet:</strong> Skjul
                utilgjengelige drikker eller merk dem tydelig.
              </li>

              <li className="text__seo-li">
                <strong>Fremhev anbefalinger:</strong> Ikke alle drikker trenger
                samme visuelle vekt.
              </li>
            </ol>

            <p>
              I en digital meny kan denne strukturen justeres senere uten nye
              trykkekostnader.
            </p>
          </div>
        </section>

        <section id="updates" className="acquaintance">
          <h2>Hva kan en bar oppdatere raskere online?</h2>

          <p>
            Den største fordelen er ikke bare presentasjon, men vedlikehold. En
            bar kan oppdatere innhold uten å trykke en ny drikkemeny hver gang.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Endre priser.</li>
            <li className="text__seo-li">Legge til nye cocktails.</li>
            <li className="text__seo-li">Skjule utilgjengelige viner.</li>
            <li className="text__seo-li">
              Fremheve alkoholfrie drikker.
            </li>
            <li className="text__seo-li">Lage sesongtilbud.</li>
            <li className="text__seo-li">Legge til happy hour-tider.</li>
            <li className="text__seo-li">Forbedre beskrivelser.</li>
            <li className="text__seo-li">Bytte bilder.</li>
          </ul>

          <h3>QR-koder kan støtte drikkeanbefalinger</h3>

          <p>
            For barer trenger ikke den digitale menyen å erstatte alle
            personlige anbefalinger. Den kan legge til nyttige detaljer uten å
            gjøre den trykte menyen for overfylt.
          </p>

          <p>
            Dette er spesielt nyttig for drikker som krever forklaring, for
            eksempel vin, cocktails, sesongdrikker eller alkoholfrie alternativer.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvilke feil skjer med digitale drikkemenyer?</h2>

            <p>
              En digital drikkemeny kan være nyttig, men den bør ikke bli rotete,
              utdatert eller for salgsorientert.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    For mange drikker uten tydelige kategorier.
                  </li>

                  <li className="text__seo-li">
                    Utilgjengelige viner eller drikker forblir synlige.
                  </li>

                  <li className="text__seo-li">
                    Happy hour-tilbud er uklare i tid.
                  </li>

                  <li className="text__seo-li">
                    Beskrivelser er for lange eller for poetiske.
                  </li>

                  <li className="text__seo-li">
                    Alkoholfrie alternativer er skjult.
                  </li>

                  <li className="text__seo-li">
                    Priser holdes ikke oppdatert.
                  </li>

                  <li className="text__seo-li">
                    QR-koden erstatter helt personalets anbefalinger.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Bruk tydelige kategorier.</li>

                  <li className="text__seo-li">
                    Skjul utilgjengelige drikker med en gang.
                  </li>

                  <li className="text__seo-li">
                    Merk tidsbegrensede tilbud tydelig.
                  </li>

                  <li className="text__seo-li">
                    Skriv korte og nyttige beskrivelser.
                  </li>

                  <li className="text__seo-li">
                    Gjør alkoholfrie alternativer synlige.
                  </li>

                  <li className="text__seo-li">
                    Kontroller priser regelmessig.
                  </li>

                  <li className="text__seo-li">
                    Bruk den digitale menyen som et supplement til service.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Hvordan ville jeg startet som bareier?</h2>

          <p>
            Jeg ville ikke digitalisert hele drikkemenyen med en gang. Jeg ville
            startet med den seksjonen som endres oftest eller trenger mest
            forklaring.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Velg én hovedkategori:</strong> For eksempel cocktails,
              vin eller alkoholfrie drikker.
            </li>

            <li className="text__seo-li">
              <strong>Legg til 10 til 20 drikker:</strong> Med pris og kort
              beskrivelse.
            </li>

            <li className="text__seo-li">
              <strong>Marker anbefalinger:</strong> Ikke alle drikker bør føles
              like viktige.
            </li>

            <li className="text__seo-li">
              <strong>Sjekk på smarttelefon:</strong> Menyen må være rask og
              enkel å lese.
            </li>

            <li className="text__seo-li">
              <strong>Test tilgjengelighet:</strong> Skjul én drikk og aktiver
              den igjen senere.
            </li>

            <li className="text__seo-li">
              <strong>Utvid etterpå:</strong> Legg til flere kategorier først
              etter den første ekte testen.
            </li>
          </ol>

          <p>
            Slik holder drikkemenyen seg oversiktlig i stedet for å bli en lang
            og vanskelig liste.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva bør du gjøre nå?</h2>

            <p>
              Hvis du vil lage en digital drikkemeny for baren din, start med en
              liten test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Opprett en digital meny.</li>
              <li className="text__seo-li">Legg til én drikkekategori.</li>
              <li className="text__seo-li">Legg inn 10 til 20 ekte drikker.</li>
              <li className="text__seo-li">
                Legg til korte beskrivelser og priser.
              </li>
              <li className="text__seo-li">
                Åpne menyen via QR-koden på en smarttelefon.
              </li>
              <li className="text__seo-li">
                Skjul én utilgjengelig drikk som test.
              </li>
              <li className="text__seo-li">
                Utvid deretter med cocktails, vin eller alkoholfrie alternativer.
              </li>
            </ul>

            <VisualIdea>
              En liten barmeny-test: én drikkekategori, 10 ekte drikker,
              synlige priser, korte beskrivelser og én utilgjengelig drikk
              skjult fra den offentlige menyen.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="no" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ om digitale drikkemenyer</h2>

          <div className="seoFaq">
            <details>
              <summary>Hva er en digital drikkemeny?</summary>

              <p>
                Det er en online-administrert meny for cocktails, vin,
                alkoholfrie drikker, kaffe, øl eller sesongtilbud som gjester
                kan åpne med QR-kode.
              </p>
            </details>

            <details>
              <summary>Er en digital drikkemeny bare nyttig for barer?</summary>

              <p>
                Nei. Den kan også hjelpe restauranter, kafeer, hoteller,
                vinbarer, cocktailbarer eller eventlokaler.
              </p>
            </details>

            <details>
              <summary>Må jeg fjerne den trykte drikkemenyen?</summary>

              <p>
                Ikke nødvendigvis. En kombinasjon fungerer ofte best: trykt meny
                for rask oversikt og QR-kode for aktuell eller mer detaljert
                informasjon.
              </p>
            </details>

            <details>
              <summary>Kan jeg skjule utilgjengelige drikker?</summary>

              <p>
                Ja. Utilgjengelige drikker kan skjules midlertidig og aktiveres
                igjen senere.
              </p>
            </details>

            <details>
              <summary>Kan jeg fremheve alkoholfrie alternativer?</summary>

              <p>
                Ja. En digital meny er nyttig for å gjøre alkoholfrie drikker,
                sesongdrikker eller anbefalinger mer synlige.
              </p>
            </details>

            <details>
              <summary>Forblir QR-koden den samme?</summary>

              <p>
                Ja, hvis QR-koden peker til en stabil menylenke. Du kan
                oppdatere innhold uten å trykke QR-koden på nytt.
              </p>
            </details>
          </div>
        </section>

        <SeoRelatedLinks
          lang="no"
          group="menu"
          exclude="/no/digital-drikkemeny-bar"
        />
      </article>
    </main>
  );
}